import { Typography } from '@mui/material'

const HelpText = () => (
  <div className='p-5'>
    <Typography variant='h5' className='pb-5'>Create Passes</Typography>
    <p>
      Use this page to create test passes while designing a template, or live passes for your customers.
      Review test passes on real devices because Apple and Google may format some content differently from the previews.
    </p>

    <Typography variant='h5' className='pt-10 pb-5'>Standard templates</Typography>
    <p>Standard templates use the complete six-step process:</p>
    <ol className='list-decimal pl-8'>
      <li><b>Choose template:</b> Select the template to use. It may already be selected when you arrive from another page.</li>
      <li><b>Pass mode:</b> Choose whether to create a TEST pass or LIVE passes.</li>
      <li><b>Upload data:</b> Live passes require a JSON, CSV or Excel data file. Uploading data for a test pass is optional.</li>
      <li><b>Pass values:</b> For a test pass, use the template values or enter values manually. Uploaded data is reviewed in a table.</li>
      <li><b>Confirm:</b> Check the template, mode, number of passes and values before creating them.</li>
      <li><b>Results:</b> Review the created passes, copy their install URLs, and issue more passes if required.</li>
    </ol>

    <Typography variant='h6' className='pt-8 pb-4'>Pass limits</Typography>
    <p>
      This form creates one test pass per test request. Live uploads can contain up to 100 passes; the upload is processed in smaller batches automatically.
      If a file contains more than the limit, split it into smaller files and upload them separately.
    </p>

    <Typography variant='h6' className='pt-8 pb-4'>Uploaded data review</Typography>
    <p>
      The review table displays only variables supplied in the uploaded data. Values are checked against the variable type,
      required-value rules, read-only rules and the variables defined by the template. Unknown variables and invalid values must be corrected before continuing.
    </p>
    <ul className='list-disc pl-8'>
      <li><b className='text-green-500'>Green:</b> valid uploaded value accepted for the pass.</li>
      <li><b className='text-orange-500'>Orange:</b> valid value matches the template default. It will still be used as uploaded.</li>
      <li><b className='text-red-500'>Red:</b> value has an invalid type or fails a required-value rule.</li>
      <li><b className='text-blue-500'>Blue:</b> unexpected variable that is not defined by the template or is read-only. Continue is disabled until it is removed from the upload.</li>
    </ul>
    <p className='mb-10'>
      If the data is not correct, discard the upload and upload a corrected file. Uploaded files can be JSON, CSV or MS Excel format.
    </p>

    <Typography variant='h5' className='pt-10 pb-5'>Managed templates</Typography>
    <p className='mb-10'>
      Managed templates use the same six-step layout, but Upload data and Pass values are skipped because the managed pass type controls those details.
      The skipped steps are shown in grey. You still choose the template and pass mode, confirm the request, and review the results.
    </p>

    <Typography variant='h6' className='pt-8 pb-4'>Managed loyalty cards</Typography>
    <p className='mb-10'>
      Test loyalty cards can be created directly from the confirmation step. Live loyalty cards are normally self-issued by customers
      using the signup page and QR code provided for the template.
    </p>

    <Typography variant='h6' className='pt-8 pb-4'>Managed stampcards</Typography>
    <p className='mb-10'>
      Test stampcards can be created directly from the confirmation step. Live stampcards are created through the live signup page,
      which can be opened from the confirmation step or visited using its QR code.
    </p>

    <Typography variant='h6' className='pt-8 pb-4'>Managed giftcards</Typography>
    <p className='mb-10'>
      Test giftcards can be created directly from the confirmation step. For live giftcards, enter the gift amount and choose whether
      the card should have an expiry date before creating it.
    </p>
  </div>
)

export default HelpText
